import React, { useMemo } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, isValid } from 'date-fns';
import { nl } from 'date-fns/locale';
import { ForecastChangedProduct } from '@domain/tasks/models/Task';
import { APP_CONFIG } from '../../../../../config/app.config';
import {
  CHART_HEIGHT,
  buildCompareChartData,
  chartXTicks,
  currentIsoWeek,
  todayDateFromChart,
} from './chart';

interface ForecastCompareChartProps {
  product: ForecastChangedProduct | null;
}

const ForecastCompareChart: React.FC<ForecastCompareChartProps> = ({ product }) => {
  const theme = useTheme();
  const colors = APP_CONFIG.ui.theme.colors;
  const nowWeek = useMemo(() => currentIsoWeek(), [product]);
  const chartData = useMemo(
    () => buildCompareChartData(product, nowWeek),
    [product, nowWeek],
  );
  const todayDate = todayDateFromChart(chartData, nowWeek);
  const xTicks = useMemo(
    () => chartXTicks(chartData, todayDate),
    [chartData, todayDate],
  );

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ height: 24 }}
      >
        <Stack direction="row" gap="10px" alignItems="center">
          <LegendDot color={colors.borderHover} label="Old forecast" />
          <LegendDot color={colors.primary} label="New Forecast" />
        </Stack>
      </Stack>

      {chartData.length > 0 ? (
        <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="forecastChangedHistoricalFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.secondary} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={colors.secondary} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={colors.border} vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                ticks={xTicks}
                interval={0}
                height={28}
                tick={(props) => (
                  <DateTick
                    {...props}
                    todayDate={todayDate}
                    color={colors.primaryText}
                    fontFamily={theme.typography.fontFamily}
                  />
                )}
              />
              <YAxis domain={[0, 'auto']} orientation="left" width={20} axisLine={false} tickLine={false}  fontSize={12} fontWeight={400} fontFamily={theme.typography.fontFamily} /
              
              
              >
              {todayDate && (
                <ReferenceLine x={todayDate} stroke={colors.secondary} strokeWidth={1} />
              )}
              <Tooltip content={(props: any) => <CompareTooltip {...props} />} />
              <Area
                type="monotone"
                dataKey="historical"
                name="Sales"
                stroke={colors.secondary}
                strokeWidth={3}
                fill="url(#forecastChangedHistoricalFill)"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
                legendType="none"
              />
              <Line
                type="monotone"
                dataKey="previous"
                name="Old forecast"
                stroke={colors.borderHover}
                strokeWidth={3}
                strokeDasharray="6 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="current"
                name="New Forecast"
                stroke={colors.primary}
                strokeWidth={3}
                strokeDasharray="6 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography variant="body2" color={colors.secondaryText} sx={{ py: 4 }}>
          Forecast comparison data is not available for this product.
        </Typography>
      )}
    </Stack>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" alignItems="center" gap={1}>
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: 12,
        lineHeight: '16px',
        letterSpacing: '0.5px',
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Stack>
);

const DateTick = ({
  x,
  y,
  payload,
  todayDate,
  color,
  fontFamily,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  todayDate?: string;
  color: string;
  fontFamily?: string;
}) => {
  const raw = payload?.value;
  const date = raw ? new Date(`${raw}T12:00:00`) : null;
  if (!date || !isValid(date)) return null;

  return (
    <text
      x={x}
      y={(y ?? 0) + 12}
      textAnchor="middle"
      fill={color}
      fontSize={10}
      fontWeight={raw === todayDate ? 600 : 400}
      fontFamily={fontFamily}
    >
      {format(date, 'd MMMM', { locale: nl })}
    </text>
  );
};

const CompareTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: {
    name: string;
    value: number;
    color: string;
    dataKey?: string;
    payload?: { week: number; year: number; date: string };
  }[];
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  if (!active || !payload?.length) return null;

  const dataPoint = payload[0]?.payload;
  const date = dataPoint?.date ? new Date(`${dataPoint.date}T12:00:00`) : null;
  const hasForecast = payload.some(
    (item) =>
      (item.dataKey === 'previous' || item.dataKey === 'current') &&
      item.value != null,
  );
  const series = payload.filter((item) => {
    if (item.value == null) return false;
    if (hasForecast) return item.dataKey === 'previous' || item.dataKey === 'current';
    return item.dataKey === 'historical';
  });

  if (series.length === 0) return null;

  return (
    <Stack
      spacing={1}
      sx={{
        bgcolor: colors.background,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.08)',
      }}
    >
      {date && isValid(date) && (
        <Typography variant="body2" color="text.secondary">
          {format(date, 'd MMMM', { locale: nl })}
        </Typography>
      )}
      {series.map((item) => (
        <Stack key={item.name} direction="row" justifyContent="space-between" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {item.name}
          </Typography>
          <Typography variant="body2" sx={{ color: item.color, fontWeight: 600 }}>
            {Math.round(item.value ?? 0)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

export default ForecastCompareChart;
